// AdaptiveFilter — three toggleable "chips" above the e-bike carousel.
// Golden-glow highlighting: matching bikes get a brass ring on their card
// while non-matching bikes stay visible (no hiding). Users see the whole range
// AND get their fit steered — best of both worlds.
//
// The "Why?" panel below the chips explains, in plain human terms, why each
// design choice matters to a customer. This is user-centric design copy
// dictated by the founder himself.

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ADAPTIVE } from '@/constants/testIds';

export const ADAPTIVE_TAGS = [
  {
    key: 'step-through',
    label: 'Step-through',
    why:
      "When you're wearing waterproofs, wellies and you're soaking wet, there's no high swing-over needed. Fitter riders love it after a long estate day too.",
  },
  {
    key: 'low-standover',
    label: 'Low stand-over',
    why:
      "Handy for shorter riders, folks with hip mobility issues, or anyone pausing on rough ground where a foot down needs to reach.",
  },
  {
    key: 'one-handed-brake',
    label: 'One-handed brake',
    why:
      "For riders with prosthetics, hand injuries, or anyone who values single-handed control on a steep descent when the other hand's on the signalling.",
  },
];

export default function AdaptiveFilter({ activeTag, onChange }) {
  const [openWhy, setOpenWhy] = useState(false);
  const active = ADAPTIVE_TAGS.find((t) => t.key === activeTag) || null;

  const handleClick = (key) => {
    if (activeTag === key) {
      onChange(null);
      setOpenWhy(false);
    } else {
      onChange(key);
      setOpenWhy(true);
    }
  };

  return (
    <div data-testid={ADAPTIVE.section} className="mb-8">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <span className="font-eyebrow text-[var(--brand-muted)] text-[10px] mr-1">Find your fit ·</span>
        {ADAPTIVE_TAGS.map((tag) => {
          const isActive = activeTag === tag.key;
          return (
            <button
              key={tag.key}
              type="button"
              data-testid={ADAPTIVE.chip(tag.key)}
              aria-pressed={isActive}
              onClick={() => handleClick(tag.key)}
              className={
                'inline-flex items-center gap-2 border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-all duration-200 ' +
                (isActive
                  ? 'bg-[var(--brand-gold)] border-[var(--brand-gold)] text-[var(--brand-forest-deep)] shadow-[0_0_0_3px_rgba(197,179,88,0.25)]'
                  : 'border-[var(--brand-gold-soft)] text-[var(--brand-forest)] bg-transparent hover:bg-[var(--brand-gold)]/10')
              }
            >
              {tag.label}
            </button>
          );
        })}
        {active && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="ml-auto text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--brand-muted)] hover:text-[var(--brand-forest)] transition-colors"
          >
            Clear ×
          </button>
        )}
      </div>

      {active && (
        <div
          data-testid={ADAPTIVE.why}
          className="mt-4 border-l-2 border-[var(--brand-gold)] pl-4 py-2 bg-[var(--brand-gold)]/5"
        >
          <p className="text-xs uppercase tracking-[0.20em] font-semibold text-[var(--brand-olive)] mb-1.5">
            Why {active.label.toLowerCase()} matters
          </p>
          <p className="text-sm text-[var(--brand-ink)] leading-relaxed italic">
            {active.why}
          </p>
        </div>
      )}
    </div>
  );
}
